import MIcon from '../../components/common/MIcon';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import './frontdesk.css';
import { LogoUNEA } from '../../components/common/Logo';
import Icon from '../../components/common/Icon';
import { useT } from '../../context/TenantContext';
import { Button } from '../../components/ui';

const STATUS_ORDER = { open:0, in_progress:1, escalated:2, scheduled:2, resolved:3, forwarded:3 };
const PRIO_C  = { low:'#15803D', medium:'#B45309', high:'#0085CA', critical:'#B91C1C' };
const PRIO_BG = { low:'#E8F5E9', medium:'#FFF3E0', high:'#E8F0FE', critical:'#FFEBEE' };

const SNACKBAR_COLORS = {
  success: { bg:'#F0FDF4', border:'#86EFAC', text:'#15803D', iconBg:'#DCFCE7' },
};

function Snackbar({ visible, color='success', icon, onClose, children }) {
  const col = SNACKBAR_COLORS[color];
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      background:col.bg, border:`1px solid ${col.border}`,
      borderRadius:8, boxShadow:'0 2px 12px rgba(0,0,0,.08)',
      padding:'10px 12px 10px 14px',
      display:'flex', alignItems:'center', gap:10,
      fontSize:13, color:col.text, fontFamily:'var(--font-body)',
      transition:'opacity .25s, transform .25s',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(10px)',
      maxWidth:320, minWidth:220,
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      <span style={{width:18,height:18,borderRadius:'50%',background:col.iconBg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,flexShrink:0,fontWeight:700}}>{icon}</span>
      <span style={{flex:1,lineHeight:1.4,fontSize:12}}>{children}</span>
      <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:col.text,padding:'0 2px',lineHeight:1,flexShrink:0,opacity:.6,display:'flex'}}><Icon name="close" size={14} /></button>
    </div>
  );
}

export default function TrackTicket() {
  const { ref }    = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();
  const showToast  = location.state?.showToast || false;
  const { t }      = useT();

  const leftRef  = React.useRef(null);
  const rightRef = React.useRef(null);
  const [ticket,      setTicket]      = useState(null);
  const [comments,    setComments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [toast,       setToast]       = useState(showToast);
  const [replyBody,   setReplyBody]   = useState('');
  const [replySending,setReplySending]= useState(false);
  const [replyDone,   setReplyDone]   = useState(false);

  // Steps definidos dentro del componente para usar t()
  const STATUS_STEPS = [
    { key:'open',        label: t('track.step_open'),      desc: t('track.step_open_desc') },
    { key:'in_progress', label: t('track.step_progress'),  desc: t('track.step_progress_desc') },
    { key:'escalated',   label: t('track.step_escalated'), desc: t('track.step_escalated_desc') },
    { key:'resolved',    label: t('track.step_resolved'),  desc: t('track.step_resolved_desc') },
  ];

  const PRIO = {
    low:      t('track.prio_low'),
    medium:   t('track.prio_medium'),
    high:     t('track.prio_high'),
    critical: t('track.prio_critical'),
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setToast(false), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (leftRef.current && rightRef.current) {
      const h = Math.max(leftRef.current.offsetHeight, rightRef.current.offsetHeight);
      leftRef.current.style.minHeight  = h + 'px';
      rightRef.current.style.minHeight = h + 'px';
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const res   = await fetch(`/api/tickets/public?search=${encodeURIComponent(ref)}`).then(r => r.json());
        const upper = ref.toUpperCase();
        const found = (res.data || []).find(t => t.ref.toUpperCase() === upper)
                   || (res.data || []).find(t => t.ref.toUpperCase().includes(upper));
        if (!found) throw new Error(t('track.not_found_title'));
        const cmts = await api.getComments(found.id).catch(() => []);
        setTicket(found);
        setComments(cmts.filter(c => !c.is_internal));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [ref]);

  const sendReply = async () => {
    if (!replyBody.trim() || !ticket) return;
    setReplySending(true);
    try {
      const res = await fetch(`/api/tickets/${ticket.id}/comments/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: replyBody.trim(), author_name: ticket.requester || 'Usuario' }),
      });
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [...prev, newComment]);
        setReplyBody('');
        setReplyDone(true);
        setTimeout(() => setReplyDone(false), 4000);
      }
    } finally {
      setReplySending(false);
    }
  };

  const currentIdx   = ticket ? STATUS_ORDER[ticket.status] : 0;
  const visibleSteps = ticket
    ? STATUS_STEPS.filter(s => s.key !== 'escalated' || ticket.status === 'escalated')
    : STATUS_STEPS.filter(s => s.key !== 'escalated');

  // Locale para fechas según idioma del tenant
  const dateLocale = t('track.step_open') === 'Aberto' ? 'pt-PT'
                   : t('track.step_open') === 'Open'   ? 'en-GB'
                   : 'es-ES';

  return (
    <div className="fd-root">
      {toast && (
        <Snackbar visible={toast} color="success" icon="✓" onClose={() => setToast(false)}>
          {t('track.toast')}
        </Snackbar>
      )}

      <header className="fd-appbar">
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32} style={{display:'block'}} />
          <div style={{width:1,height:28,background:'var(--fd-border)',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:400,color:'var(--fd-text)'}}>HelpDesk TI</span>
        </div>
      </header>

      <div className="fd-two-col" style={{alignItems:"start"}}>

        {/* ── CARD IZQUIERDO ── */}
        <div ref={leftRef} className="fd-card fd-card-side">
          {loading && <div style={{color:'var(--fd-muted)',fontSize:13}}>{t('track.loading')}</div>}
          {error   && <div style={{color:'#B91C1C',fontSize:13}}>{error}</div>}

          {ticket && (<>
            {/* Título + estado */}
            <h2 style={{fontSize:18,fontWeight:700,color:'var(--fd-text)',marginBottom:4,lineHeight:1.3}}>
              {t('track.title')}
            </h2>
            <h2 style={{fontSize:20,fontWeight:700,color:'var(--fd-accent)',marginBottom:8,lineHeight:1.3}}>
              {ticket.status === 'forwarded'
                ? t('track.forwarded_status')
                : (STATUS_STEPS.find(s => s.key === ticket.status)?.label || t('track.in_process'))}
            </h2>
            <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.6,marginBottom:20}}>
              {ticket.status === 'forwarded'
                ? t('track.forwarded_desc')
                : STATUS_STEPS.find(s => s.key === ticket.status)?.desc}
            </p>

            {/* Ref + prioridad */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:8}}>
              <div style={{fontFamily:'monospace',fontSize:15,fontWeight:700,color:'var(--fd-accent)',letterSpacing:.5}}>
                {ticket.ref}
              </div>
              <span style={{
                fontSize:11, fontWeight:600, color:PRIO_C[ticket.priority],
                background:PRIO_BG[ticket.priority], padding:'0 8px',
                borderRadius:20, height:20, display:'inline-flex', alignItems:'center', flexShrink:0,
              }}>
                {PRIO[ticket.priority]}
              </span>
            </div>

            <div style={{height:1,background:'var(--fd-border)',margin:'16px 0'}}/>

            {/* Agente asignado */}
            <div style={{marginBottom:16}}>
              <div className="fd-side-section-title" style={{marginBottom:10}}>{t('track.agent_assigned')}</div>
              {ticket.assignee_name ? (
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:36,height:36,borderRadius:'50%',background:'var(--una-blue-lt)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'var(--fd-accent)',flexShrink:0}}>
                    {ticket.assignee_name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--fd-text)'}}>{ticket.assignee_name}</div>
                    <div style={{fontSize:11,color:'var(--fd-muted)'}}>{t('track.support_ti')} · N{ticket.level}</div>
                  </div>
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:36,height:36,borderRadius:'50%',background:'#F5F5F5',border:'1px dashed #E0E0E0',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <Icon name="schedule" size={18} style={{color:'#9E9E9E'}} />
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:'var(--fd-text)'}}>{t('track.pending_assign')}</div>
                    <div style={{fontSize:11,color:'var(--fd-muted)'}}>{t('track.pending_assign_desc')}</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{height:1,background:'var(--fd-border)',margin:'0 0 16px'}}/>

            {/* ¿Qué pasa después? */}
            <div>
              <div className="fd-side-section-title" style={{marginBottom:10}}>{t('track.what_next')}</div>
              <ol style={{paddingLeft:16,display:'flex',flexDirection:'column',gap:8}}>
                {[
                  [t('track.step_reception'), t('track.step_reception_desc')],
                  [t('track.step_assign'),    t('track.step_assign_desc')],
                  [t('track.step_resolution'),t('track.step_resolution_desc')],
                  [t('track.step_close'),     t('track.step_close_desc')],
                ].map(([title, desc]) => (
                  <li key={title} style={{fontSize:12,color:'var(--fd-muted)',lineHeight:1.5}}>
                    <strong style={{color:'var(--fd-text)'}}>{title}</strong> — {desc}
                  </li>
                ))}
              </ol>
            </div>
          </>)}
        </div>

        {/* ── CARD DERECHO ── */}
        <div ref={rightRef} className="fd-card fd-card-main">

          {loading && (
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'40px 28px',color:'var(--fd-muted)'}}>
              <div className="fd-spinner" style={{width:20,height:20,borderWidth:2}}/>
              {t('track.searching')}
            </div>
          )}

          {error && (
            <div style={{padding:'40px 28px',textAlign:'center'}}>
              <div style={{fontSize:36,marginBottom:12}}>🔍</div>
              <div style={{fontSize:16,fontWeight:600,color:'var(--fd-text)',marginBottom:6}}>{t('track.not_found_title')}</div>
              <div style={{fontSize:13,color:'var(--fd-muted)',marginBottom:20}}>{error}</div>
              <Button variant="primary" onClick={() => navigate('/')}>
                {t('track.open_new')}
              </Button>
            </div>
          )}

          {ticket && (<>
            <div className="fd-form-header">
              <h2 style={{fontSize:16,fontWeight:600,color:'var(--fd-text)',marginBottom:0}}>{t('track.info_title')}</h2>
            </div>

            {/* Datos resumen */}
            <div style={{padding:'16px 24px 0',display:'flex',flexDirection:'column',gap:12}}>
              <div>
                <div className="fd-summary-label">{t('track.field_title')}</div>
                <div className="fd-summary-value" style={{fontWeight:500}}>{ticket.title}</div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <div className="fd-summary-label">{t('track.field_requester')}</div>
                  <div className="fd-summary-value">{ticket.requester}</div>
                </div>
                <div>
                  <div className="fd-summary-label">{t('track.field_email')}</div>
                  <div className="fd-summary-value">{ticket.requester_email || '—'}</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <div className="fd-summary-label">{t('track.field_level')}</div>
                  <div className="fd-summary-value">{t('track.field_level_val')} {ticket.level}</div>
                </div>
                <div>
                  <div className="fd-summary-label">{t('track.field_created')}</div>
                  <div className="fd-summary-value">
                    {new Date(ticket.created_at).toLocaleString(dateLocale, {day:'2-digit',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline horizontal */}
            <div style={{padding:'20px 40px 0 24px'}}>
              <div className="fd-summary-label" style={{marginBottom:16}}>{t('track.status_label')}</div>
              <div style={{display:'flex',alignItems:'flex-start'}}>
                {visibleSteps.map((step, i) => {
                  const displayStatus = ['closed','forwarded'].includes(ticket.status) ? 'resolved' : ticket.status;
                  const stepIdx   = STATUS_ORDER[step.key];
                  const isDone    = stepIdx < STATUS_ORDER[displayStatus];
                  const isCurrent = step.key === displayStatus;
                  const isPending = stepIdx > STATUS_ORDER[displayStatus];
                  const isLast    = i === visibleSteps.length - 1;
                  const dotColor  = isDone || isCurrent ? 'var(--fd-accent)' : '#E0E0E0';
                  const dotBg     = isDone ? 'var(--fd-accent)' : isCurrent ? '#fff' : '#F5F5F5';

                  return (
                    <React.Fragment key={step.key}>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'center',flex: isLast ? 0 : 1, minWidth:50}}>
                        <div style={{
                          width:24, height:24, borderRadius:'50%',
                          border:`2px solid ${dotColor}`,
                          background:dotBg,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          flexShrink:0,
                          boxShadow: isCurrent ? '0 0 0 3px var(--una-blue-lt)' : 'none',
                          zIndex:1,
                        }}>
                          {isDone && <span style={{color:'#fff',fontSize:10,fontWeight:700,lineHeight:1}}>✓</span>}
                          {isCurrent && <span style={{width:8,height:8,borderRadius:'50%',background:'var(--fd-accent)',display:'block'}}/>}
                        </div>
                        {(() => {
                          const tsMap = {
                            open:        ticket.created_at,
                            in_progress: isDone || isCurrent ? ticket.updated_at : null,
                            resolved:    ticket.resolved_at,
                          };
                          const ts   = (isDone || isCurrent) ? tsMap[step.key] : null;
                          const fmtTs = ts ? new Date(ts).toLocaleString(dateLocale, {day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'}) : null;
                          return (
                            <div style={{textAlign:'center',marginTop:6,whiteSpace:'nowrap'}}>
                              <div style={{fontSize:11,fontWeight: isCurrent ? 600 : 400, color: isPending ? '#9CA3AF' : 'var(--fd-text)',lineHeight:1.3}}>
                                {step.label}
                              </div>
                              {fmtTs && (
                                <div style={{fontSize:10,color:'var(--fd-muted)',marginTop:2,lineHeight:1.3}}>
                                  {fmtTs}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {!isLast && (
                        <div style={{height:2,flex:1,marginTop:11,background: isDone ? 'var(--fd-accent)' : '#E0E0E0',minWidth:12,flexShrink:0}}/>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Descripción */}
            {ticket.description && (
              <div style={{padding:'20px 24px 0'}}>
                <div className="fd-summary-label" style={{marginBottom:6}}>{t('track.description')}</div>
                <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{ticket.description}</p>
              </div>
            )}

            {/* Comentarios */}
            <div style={{padding:'20px 24px 24px'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
                <div className="fd-summary-label" style={{marginBottom:0}}>{t('track.updates_title')}</div>
                {comments.length > 0 && (
                  <span style={{background:'var(--una-blue-lt)',color:'var(--fd-accent)',borderRadius:10,padding:'1px 7px',fontSize:11,fontWeight:700}}>
                    {comments.length}
                  </span>
                )}
              </div>

              {comments.length === 0 ? (
                <div style={{fontSize:13,color:'var(--fd-muted)',fontStyle:'italic',padding:'8px 0'}}>
                  {t('track.no_updates')}
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {[...comments].reverse().map(c => (
                    <div key={c.id} style={{display:'flex',gap:10,padding:'12px 14px',background:'var(--fd-bg)',borderRadius:'var(--radius-sm)',border:'1px solid var(--fd-border)'}}>
                      <div style={{width:32,height:32,borderRadius:'50%',background:'var(--fd-accent)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0}}>
                        {c.author_name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                          <span style={{fontSize:13,fontWeight:600,color:'var(--fd-text)'}}>{c.author_name}</span>
                          <span style={{fontSize:11,color:'var(--fd-accent)',background:'var(--una-blue-lt)',padding:'1px 7px',borderRadius:10}}>{t('track.support_ti')}</span>
                          <span style={{fontSize:11,color:'var(--fd-muted)',marginLeft:'auto'}}>
                            {new Date(c.created_at).toLocaleDateString(dateLocale, {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                          </span>
                        </div>
                        <div style={{fontSize:13,color:'var(--fd-text)',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{c.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Banner resuelto */}
              {(ticket.status === 'resolved' || ticket.status === 'forwarded') && (
                <div style={{marginTop:16,padding:'16px 20px',background:'#F0FDF4',borderRadius:10,display:'flex',alignItems:'center',gap:14}}>
                  <span style={{flexShrink:0,fontSize:20,color:'#15803D',lineHeight:1}}>✓</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#15803D'}}>{t('track.resolved_banner')}</div>
                    <div style={{fontSize:12,color:'#16A34A',marginTop:3}}>{t('track.resolved_banner_sub')}</div>
                  </div>
                </div>
              )}

              {/* Formulario de respuesta */}
              {ticket.status !== 'resolved' && ticket.status !== 'forwarded' && (
                <div style={{marginTop:20,padding:'16px',background:'var(--fd-bg)',borderRadius:10,border:'1px solid var(--fd-border)'}}>
                  <div className="fd-summary-label" style={{marginBottom:8}}>{t('track.reply_title')}</div>
                  <textarea
                    value={replyBody}
                    onChange={e => setReplyBody(e.target.value)}
                    placeholder={t('track.reply_placeholder')}
                    rows={3}
                    style={{
                      width:'100%', boxSizing:'border-box',
                      border:'1px solid var(--fd-border)', borderRadius:8,
                      padding:'10px 12px', fontSize:13, fontFamily:'var(--font-body)',
                      resize:'vertical', outline:'none', color:'var(--fd-text)',
                      background:'#fff', lineHeight:1.5,
                    }}
                  />
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:8}}>
                    {replyDone
                      ? <span style={{fontSize:12,color:'#15803D'}}>{t('track.reply_done')}</span>
                      : <span style={{fontSize:12,color:'var(--fd-muted)'}}>{t('track.reply_hint')}</span>
                    }
                    <button
                      onClick={sendReply}
                      disabled={replySending || !replyBody.trim()}
                      style={{
                        height:34, padding:'0 16px', fontSize:12, fontWeight:600,
                        background: replyBody.trim() ? 'var(--fd-accent)' : '#E5E7EB',
                        color: replyBody.trim() ? '#fff' : '#9CA3AF',
                        border:'none', borderRadius:8, cursor: replyBody.trim() ? 'pointer' : 'default',
                        transition:'background .15s',
                      }}
                    >
                      {replySending ? t('track.reply_sending') : t('track.reply_send')}
                    </button>
                  </div>
                </div>
              )}

              <div style={{display:'flex',justifyContent:'flex-end',marginTop:24}}>
                <Button variant="secondary" onClick={() => navigate('/')}>
                  {t('track.back')}
                </Button>
              </div>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
